/**
 * Hosted private tasks v1 (P6): the browser client for owner-approved hosted
 * model tasks — `POST/GET /v1/projects/{id}/tasks`, `GET …/tasks/{task_id}`, and
 * `GET …/tasks/options` (the models a run can use, with prices).
 *
 * The approval is never a value the browser holds: the request carries
 * `approve: true` with the provider, model and spend ceiling the owner is
 * approving, and the server mints the single-run receipt bound to exactly
 * those values. There is no field here for an approval id or a key.
 */

import { requestJson, responseArray, responseRecord } from './http';

/** The per-run spend ceiling the server accepts during the internal beta (USD 5). */
export const MAX_SPEND_CEILING_MICROS = 5_000_000;

export interface HostedTaskApproval {
  approval_receipt_id: string;
  provider: string;
  model: string;
  spend_ceiling_micros: number | null;
  reasoning_effort?: string;
}

export interface HostedTask {
  task_id: string;
  project_id: string;
  status: string;
  attempts: number;
  max_attempts: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  failed: boolean;
  /** Why a failed task stopped, as a code; never the raw worker error. */
  failure?: string | null;
  prompt: string;
  approval: HostedTaskApproval;
  artifact: Record<string, unknown> | null;
}

export interface SubmitTaskInput {
  prompt: string;
  provider: string;
  model: string;
  vaultKeyId: string;
  spendCeilingMicros: number;
  idempotencyKey: string;
  reasoningEffort?: string;
}

/** A model the price book prices and the worker can bound, per million tokens. */
export interface TaskModelOption {
  provider: string;
  model: string;
  input_per_mtok: number;
  output_per_mtok: number;
  as_of: string;
  /** Catalogue details, present when the price book was built by the OpenRouter sync. */
  name?: string;
  description?: string;
  context_length?: number;
  input_modalities?: string[];
  supports_tools?: boolean;
  supports_reasoning?: boolean;
  supported_reasoning_efforts?: string[];
  opt_in_only?: boolean;
  /** Long prompts cost more; the price shown (and the spend limit) uses the higher tier. */
  tiered_pricing?: boolean;
  /** "Tested by Auteur": present only for models with a shipped dossier. */
  dossier?: TaskModelDossier;
}

export interface TaskModelDossier {
  tested_for: string[];
  strengths: { text: string; runs: number }[];
  weaknesses: { text: string; runs: number }[];
  runs: number;
  last_tested: string;
}

function dossierFrom(value: unknown): TaskModelDossier | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const items = (list: unknown) => (Array.isArray(list) ? list : [])
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .filter((item) => typeof item.text === 'string' && typeof item.runs === 'number')
    .map((item) => ({ text: String(item.text), runs: Number(item.runs) }));
  if (typeof raw.runs !== 'number') return undefined;
  return {
    tested_for: (Array.isArray(raw.tested_for) ? raw.tested_for : []).filter((t): t is string => typeof t === 'string'),
    strengths: items(raw.strengths),
    weaknesses: items(raw.weaknesses),
    runs: raw.runs,
    last_tested: typeof raw.last_tested === 'string' ? raw.last_tested : '',
  };
}

function catalogueDetails(row: Record<string, unknown>): Partial<TaskModelOption> {
  const details: Partial<TaskModelOption> = {};
  if (row.opt_in_only === true) details.opt_in_only = true;
  if (typeof row.name === 'string' && row.name) details.name = row.name;
  if (typeof row.description === 'string' && row.description) details.description = row.description;
  if (typeof row.context_length === 'number' && row.context_length > 0) details.context_length = row.context_length;
  if (Array.isArray(row.input_modalities)) {
    details.input_modalities = row.input_modalities.filter((m): m is string => typeof m === 'string');
  }
  for (const flag of ['supports_tools', 'supports_reasoning', 'tiered_pricing'] as const) {
    if (typeof row[flag] === 'boolean') details[flag] = row[flag] as boolean;
  }
  if (Array.isArray(row.supported_reasoning_efforts)) {
    details.supported_reasoning_efforts = row.supported_reasoning_efforts.filter((effort): effort is string => typeof effort === 'string');
  }
  const dossier = dossierFrom(row.dossier);
  if (dossier) details.dossier = dossier;
  return details;
}

export interface TaskOptions {
  models: TaskModelOption[];
  /** What "Tested by Auteur" is based on, in one sentence. */
  dossier_note?: string;
  price_book: { version: number; as_of: string } | null;
  max_spend_ceiling_micros: number;
}

/** Quick picks for the per-run limit, in micro-dollars. */
export const SPEND_PRESETS_MICROS: readonly number[] = [50_000, 250_000, 1_000_000];

/** Statuses after which a task will not change again. */
export const FINISHED_STATUSES: ReadonlySet<string> = new Set(['succeeded', 'failed', 'dead_letter', 'cancelled']);

export function isFinished(task: HostedTask): boolean {
  return FINISHED_STATUSES.has(task.status);
}

function taskFrom(value: unknown): HostedTask {
  const task = value as Record<string, unknown> | null;
  if (
    !task || typeof task !== 'object'
    || typeof task.task_id !== 'string' || typeof task.project_id !== 'string'
    || typeof task.status !== 'string' || typeof task.attempts !== 'number'
    || !task.approval || typeof task.approval !== 'object'
  ) {
    throw new Error('the API returned a malformed hosted task');
  }
  return task as unknown as HostedTask;
}

function tasksPath(projectId: string): string {
  return `/v1/projects/${encodeURIComponent(projectId)}/tasks`;
}

/** Convert a dollar amount typed by the owner to micro-dollars, or explain why not. */
export function usdToMicros(text: string): { micros: number } | { error: string } {
  const trimmed = text.trim();
  if (!/^\d+(\.\d{1,6})?$/.test(trimmed)) {
    return { error: 'Enter a spend limit in dollars, for example 0.25.' };
  }
  const micros = Math.round(Number(trimmed) * 1_000_000);
  if (micros <= 0) return { error: 'The spend limit must be more than $0.' };
  if (micros > MAX_SPEND_CEILING_MICROS) {
    return { error: `The spend limit can be at most $${(MAX_SPEND_CEILING_MICROS / 1_000_000).toFixed(2)} per run.` };
  }
  return { micros };
}

export function formatMicros(micros: number | null | undefined): string {
  if (typeof micros !== 'number') return 'unknown';
  return `$${(micros / 1_000_000).toFixed(micros % 10_000 === 0 ? 2 : 6)}`;
}

export async function submitTask(projectId: string, input: SubmitTaskInput): Promise<{ task: HostedTask; created: boolean }> {
  const payload = await requestJson(tasksPath(projectId), {
    method: 'POST',
    headers: { 'idempotency-key': input.idempotencyKey },
    body: JSON.stringify({
      prompt: input.prompt,
      provider: input.provider,
      model: input.model,
      vault_key_id: input.vaultKeyId,
      spend_ceiling_micros: input.spendCeilingMicros,
      ...(input.reasoningEffort ? { reasoning_effort: input.reasoningEffort } : {}),
      approve: true,
    }),
  });
  const record = responseRecord(payload);
  return { task: taskFrom(record.task), created: record.created === true };
}

export async function getTaskOptions(projectId: string): Promise<TaskOptions> {
  const record = responseRecord(await requestJson(`${tasksPath(projectId)}/options`));
  const models = (Array.isArray(record.models) ? record.models : [])
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .filter((row) => typeof row.provider === 'string' && typeof row.model === 'string'
      && typeof row.input_per_mtok === 'number' && typeof row.output_per_mtok === 'number')
    .map((row) => ({
      provider: String(row.provider),
      model: String(row.model),
      input_per_mtok: Number(row.input_per_mtok),
      output_per_mtok: Number(row.output_per_mtok),
      as_of: typeof row.as_of === 'string' ? row.as_of : '',
      ...catalogueDetails(row),
    }));
  const book = record.price_book && typeof record.price_book === 'object'
    ? record.price_book as { version: number; as_of: string } : null;
  const max = typeof record.max_spend_ceiling_micros === 'number'
    ? record.max_spend_ceiling_micros : MAX_SPEND_CEILING_MICROS;
  const note = typeof record.dossier_note === 'string' && record.dossier_note ? { dossier_note: record.dossier_note } : {};
  return { models, price_book: book, max_spend_ceiling_micros: max, ...note };
}

export async function listTasks(projectId: string): Promise<HostedTask[]> {
  return responseArray(await requestJson(tasksPath(projectId)), 'tasks').map(taskFrom);
}

export async function getTask(projectId: string, taskId: string): Promise<HostedTask> {
  const payload = await requestJson(`${tasksPath(projectId)}/${encodeURIComponent(taskId)}`);
  return taskFrom(responseRecord(payload).task);
}

/** The output text of a finished task, if the worker produced one. */
export function taskOutputText(task: HostedTask): string {
  const content = task.artifact && typeof task.artifact.content === 'object'
    ? task.artifact.content as Record<string, unknown> : null;
  return typeof content?.text === 'string' ? content.text : '';
}

/** The pending-review memory candidate the worker filed for this task, if any. */
export function taskCandidateId(task: HostedTask): string {
  const candidate = task.artifact && typeof task.artifact.candidate === 'object'
    ? task.artifact.candidate as Record<string, unknown> : null;
  return typeof candidate?.candidate_id === 'string' ? candidate.candidate_id : '';
}

/** What the run actually cost in USD (or the gateway's estimate), if known. */
export function taskSpendUsd(task: HostedTask): number | null {
  const spend = task.artifact && typeof task.artifact.spend === 'object'
    ? task.artifact.spend as Record<string, unknown> : null;
  if (typeof spend?.actual_usd === 'number') return spend.actual_usd;
  if (typeof spend?.estimated_usd === 'number') return spend.estimated_usd;
  return null;
}

/** How many approved project facts recall gave the model for this run. */
export function taskRecallCount(task: HostedTask): number {
  const recall = task.artifact && typeof task.artifact.recall === 'object'
    ? task.artifact.recall as Record<string, unknown> : null;
  return Array.isArray(recall?.facts) ? recall.facts.length : 0;
}

/** Seconds from submission to completion, or to `now` while the task is open. */
export function taskElapsedSeconds(task: HostedTask, now: number = Date.now()): number | null {
  const start = Date.parse(task.created_at);
  const end = task.completed_at ? Date.parse(task.completed_at)
    : (isFinished(task) ? Date.parse(task.updated_at) : now);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return Math.max(0, Math.round((end - start) / 1000));
}

/** Dollars for display: small amounts keep their significant digits. */
export function formatUsd(usd: number | null | undefined): string {
  if (typeof usd !== 'number' || Number.isNaN(usd)) return 'unknown';
  if (usd === 0) return '$0';
  if (usd >= 0.01) return `$${usd.toFixed(2)}`;
  const text = usd.toFixed(6).replace(/0+$/, '');
  return text === '0.' ? '<$0.000001' : `$${text}`;
}

/**
 * What a run usually costs on `modelId`: the average of this project's finished
 * runs on it when there are any, otherwise an estimate from the price book
 * (the prompt at ~4 characters a token, plus instructions and recalled facts,
 * and a short answer).
 */
export function typicalCost(
  model: TaskModelOption | null,
  modelId: string,
  promptChars: number,
  tasks: HostedTask[],
): { usd: number; basis: 'history' | 'estimate'; runs: number } | null {
  const history = tasks
    .filter((task) => task.status === 'succeeded' && task.approval.model === modelId)
    .map(taskSpendUsd)
    .filter((usd): usd is number => typeof usd === 'number');
  if (history.length > 0) {
    return { usd: history.reduce((a, b) => a + b, 0) / history.length, basis: 'history', runs: history.length };
  }
  if (!model) return null;
  const inputTokens = Math.ceil(promptChars / 4) + 400;
  const outputTokens = 300;
  return {
    usd: (inputTokens * model.input_per_mtok + outputTokens * model.output_per_mtok) / 1_000_000,
    basis: 'estimate',
    runs: 0,
  };
}

function providerName(provider: string): string {
  if (provider === 'openrouter') return 'OpenRouter';
  if (provider === 'openai') return 'OpenAI';
  if (provider === 'anthropic') return 'Anthropic';
  return provider || 'The provider';
}

/** Plain language for a failed task's code. Every run is a single attempt, never retried. */
export function taskFailureMessage(code: string | null | undefined, provider: string): string {
  const name = providerName(provider);
  switch (code) {
    case 'provider_payment_required':
      return `${name} refused the run for payment: the account is out of credit or the key has reached its limit. Nothing was charged. Add credit or raise the key's limit with ${name}, then run again.`;
    case 'provider_key_refused':
      return `${name} refused the key. Nothing was charged. Check the key in Settings, then run again.`;
    case 'provider_rate_limited':
      return `${name} is rate-limiting this key right now. Nothing was charged. Wait a minute, then run again.`;
    case 'spend_ceiling_too_low':
      return 'The spend limit was too low for this prompt, so the run stopped before calling the model. Raise the limit or shorten the prompt.';
    case 'spend_not_boundable':
      return 'This model has no price a limit can be enforced with, so the run stopped before calling it. Pick a model from the list.';
    case 'storage_unavailable':
      return 'Project storage could not take the answer, so the run stopped before calling the model. Nothing was charged. Contact support.';
    case 'provider_call_failed':
      return `The call to ${name} failed. It was not retried, so nothing further was spent.`;
    default:
      return 'This run did not finish. It was not retried, so nothing further was spent.';
  }
}

/** Plain-language messages for the refusals an owner can act on. */
export function taskErrorMessage(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  switch (code) {
    case 'job_quota_not_granted':
      return 'Hosted tasks are not enabled for your organization yet. Ask support to enable them.';
    case 'job_quota_exceeded':
      return 'Your organization has reached its task limit for now. Try again later.';
    case 'owner_required':
      return 'Only the project owner can run hosted tasks.';
    case 'approval_required':
      return 'Press “Approve & run” to approve this run and its spend limit.';
    default:
      return error instanceof Error && error.message ? error.message : 'The request failed.';
  }
}
